import {
    collection,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    getDocs,
    getDoc,
    query,
    orderBy,
    serverTimestamp,
    Timestamp,
} from 'firebase/firestore';
import { db } from './config';
import { uploadBase64ImageToCloudinary } from '../services/cloudinaryService';

// Publication data interface
export interface Publication {
    id?: string;
    title: string;
    authors: string[];
    year: string;
    conference?: string;
    journal?: string;
    location?: string;
    doi?: string;
    abstract?: string;
    keywords?: string[];
    link?: string;
    pdfUrl?: string;
    coverImage?: string;
    featured?: boolean;
    order?: number;
    createdAt?: Timestamp;
    updatedAt?: Timestamp;
}

// Collection reference
const publicationsCollection = collection(db, 'publications');

/**
 * Get all publications
 */
export const getAllPublications = async (): Promise<Publication[]> => {
    try {
        const publicationsQuery = query(publicationsCollection, orderBy('order', 'asc'));
        const querySnapshot = await getDocs(publicationsQuery);

        return querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        } as Publication));
    } catch (error) {
        console.error('Error getting publications:', error);
        throw error;
    }
};

/**
 * Get a publication by ID
 */
export const getPublicationById = async (id: string): Promise<Publication | null> => {
    try {
        const docRef = doc(db, 'publications', id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            return {
                id: docSnap.id,
                ...docSnap.data()
            } as Publication;
        } else {
            return null;
        }
    } catch (error) {
        console.error('Error getting publication:', error);
        throw error;
    }
};

/**
 * Create a new publication
 */
export const createPublication = async (publicationData: Omit<Publication, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
    try {
        // Handle cover image upload if provided
        let coverImageUrl = publicationData.coverImage;
        if (coverImageUrl && coverImageUrl.startsWith('data:')) {
            coverImageUrl = await uploadBase64ImageToCloudinary(coverImageUrl, 'publication_covers');
        }

        // Handle PDF upload if provided as base64
        let pdfUrl = publicationData.pdfUrl;
        if (pdfUrl && pdfUrl.startsWith('data:')) {
            pdfUrl = await uploadBase64ImageToCloudinary(pdfUrl, 'publication_pdfs');
        }

        // Get current max order and add 1
        const publications = await getAllPublications();
        const maxOrder = publications.length > 0
            ? Math.max(...publications.map(pub => pub.order || 0))
            : -1;

        const docRef = await addDoc(publicationsCollection, {
            ...publicationData,
            coverImage: coverImageUrl,
            pdfUrl: pdfUrl,
            order: maxOrder + 1,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        });

        return docRef.id;
    } catch (error) {
        console.error('Error creating publication:', error);
        throw error;
    }
};

/**
 * Update a publication
 */
export const updatePublication = async (id: string, publicationData: Partial<Omit<Publication, 'id' | 'createdAt' | 'updatedAt'>>): Promise<void> => {
    try {
        // Handle cover image upload if provided
        let coverImageUrl = publicationData.coverImage;
        if (coverImageUrl && coverImageUrl.startsWith('data:')) {
            coverImageUrl = await uploadBase64ImageToCloudinary(coverImageUrl, 'publication_covers');
            publicationData.coverImage = coverImageUrl;
        }

        // Handle PDF upload if provided as base64
        let pdfUrl = publicationData.pdfUrl;
        if (pdfUrl && pdfUrl.startsWith('data:')) {
            pdfUrl = await uploadBase64ImageToCloudinary(pdfUrl, 'publication_pdfs');
            publicationData.pdfUrl = pdfUrl;
        }

        const docRef = doc(db, 'publications', id);
        await updateDoc(docRef, {
            ...publicationData,
            updatedAt: serverTimestamp()
        });
    } catch (error) {
        console.error('Error updating publication:', error);
        throw error;
    }
};

/**
 * Delete a publication
 */
export const deletePublication = async (id: string): Promise<void> => {
    try {
        const docRef = doc(db, 'publications', id);
        await deleteDoc(docRef);

        // Re-order remaining entries
        await reorderPublications();
    } catch (error) {
        console.error('Error deleting publication:', error);
        throw error;
    }
};

/**
 * Reorder publications after deletion
 */
export const reorderPublications = async (): Promise<void> => {
    try {
        const publications = await getAllPublications();

        // Update order for each entry
        const updatePromises = publications.map((pub, index) => {
            if (pub.id && pub.order !== index) {
                const docRef = doc(db, 'publications', pub.id);
                return updateDoc(docRef, { order: index });
            }
            return Promise.resolve();
        });

        await Promise.all(updatePromises);
    } catch (error) {
        console.error('Error reordering publications:', error);
        throw error;
    }
};

/**
 * Update the order of publications
 */
export const updatePublicationOrder = async (reorderedPublications: { id: string; order: number }[]): Promise<void> => {
    try {
        const updatePromises = reorderedPublications.map(item => {
            const docRef = doc(db, 'publications', item.id);
            return updateDoc(docRef, { order: item.order });
        });

        await Promise.all(updatePromises);
    } catch (error) {
        console.error('Error updating publication order:', error);
        throw error;
    }
}; 